import React from 'react';
import styles from './ProfileIconButton.module.scss';
import { ApplicationResolution } from '../../applicationResolution';
import { isProfileIconImage } from '../../typeGuards';
import { logger } from '../../../common/logger';
import { getAssetUrl } from '../../utils/cloudinary';
import { cls } from '../../View/utils';
import { ProfileIcon as ProfileIconType } from '../../../common/types';
import { getTranslation } from '../../localizations/translations';
import { Language } from '@yleisradio/areena-types';
import { useAppFeaturesContext } from '../../contexts/AppFeaturesContext/AppFeaturesContext';

const fallBackButton: ProfileIconType = {
  type: 'color',
  value: 'rgba(167,167,167)',
};

export const constructEditProfileLabel = (
  languageCode: Language,
  displayName: string
): string => {
  return `${getTranslation(
    'EditProfileView_title',
    languageCode
  )}: ${displayName}`;
};

type Props = {
  icon: ProfileIconType;
  navigationId?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  text?: string;
  displayName?: string;
  imageAlt?: string;
  ariaLabelText?: string;
  textSize?: 'three-xl' | 'four-xl' | 's';
  ['data-testid']?: string;
  isEditProfileButton?: boolean;
};

/**
 * Wrap this component inside another element, which decides the icon dimensions and shape
 */
export const ProfileIconButton: React.FC<Props> = ({
  icon = fallBackButton,
  text,
  displayName,
  imageAlt,
  navigationId,
  ariaLabelText,
  onClick,
  textSize = 'three-xl',
  ['data-testid']: dataTestId,
  isEditProfileButton = false,
}) => {
  const { applicationResolution, languageCode } = useAppFeaturesContext();
  const textButtonAriaLabel = isEditProfileButton
    ? constructEditProfileLabel(languageCode, displayName || '')
    : undefined;

  const isImage = isProfileIconImage(icon);
  const imageSrc = isImage
    ? getAssetUrl(
        { id: icon.value, version: '' },
        {
          format: 'jpg',
          width:
            applicationResolution === ApplicationResolution.FULLHD ? 280 : 187,
          height:
            applicationResolution === ApplicationResolution.FULLHD ? 280 : 187,
        }
      )
    : undefined;

  if (!imageAlt && isImage) {
    logger.warn('Image profile-icon is missing alt-text');
  }

  if (!isImage && !text) {
    logger.warn('Color profile-icon is missing text');
  }

  return (
    <button
      className={cls({
        [styles.root]: true,
        [styles.threeXL]: textSize === 'three-xl',
        [styles.fourXL]: textSize === 'four-xl',
        [styles.s]: textSize === 's',
      })}
      id={navigationId}
      onClick={onClick}
      style={!isImage ? { backgroundColor: icon.value } : {}}
      data-testid={dataTestId}
      aria-label={ariaLabelText || textButtonAriaLabel}
    >
      {isImage ? <img src={imageSrc} alt={imageAlt} /> : text}
    </button>
  );
};
